import { parseAbsoluteToLocal } from "@internationalized/date"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import {
  GatedLookupSessionsPOSTRequest,
  GatedLookupSessionsPOSTSuccessResponse,
} from "ipctg-inflow-types/InFlowGatedAPI"
import { useEffect, useState } from "react"
import { GridList, GridListItem } from "react-aria-components"
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button, ButtonLoader } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Label from "@/globalComponents/atoms/Label"
import Separator from "@/globalComponents/atoms/Separator"
import Step from "@/globalComponents/atoms/Step"
import Text from "@/globalComponents/atoms/Text"
import ConfirmPlateModal from "@/globalComponents/molecules/ConfirmPlateModal"
import TermsAndConditions from "@/globalComponents/molecules/TermsAndConditions"
import TextField from "@/globalComponents/molecules/TextField"
import { GatedLookupSessionsError } from "@/utilities/expectedErrors"
import { requiredRule } from "@/utilities/form"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import useToggle from "@/utilities/hooks/useToggle"
import { normalizeLicensePlate } from "@/utilities/normalizers"

export const Route = createFileRoute("/qr/$qrLookupID/gated/register/look-up")({
  staticData: {
    pageName: "Register Vehicle",
  },
  component: RouteComponent,
})

interface RegistrationFormSchema {
  plate: string
}

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { qrLookupID } = Route.useParams()
  const { t } = useTranslation(["gated", "errors"])
  const [showModal, toggleModal] = useToggle(false)
  const [selectedPlate, setSelectedPlate] = useState("")
  const { data: me } = useSuspenseQuery(meQueryOptions())
  const form = useForm<RegistrationFormSchema>({
    defaultValues: {
      plate: "",
    },
    mode: "onBlur",
  })
  const plate = useWatch({
    control: form.control,
    name: "plate",
    defaultValue: "",
  })

  const { mutate, isPending, reset, variables } = useMutation({
    mutationFn,
    onSuccess,
    throwOnError: true,
  })

  useEffect(() => {
    form.reset(undefined, { keepValues: true })
  }, [form.formState.isSubmitSuccessful])

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("register.vehicle.header", { ns: "gated" })}
      </Heading>
      <Step step={1} of={4} />
      <Card>
        <Heading level={1} as={2}>
          {t("register.vehicle.subhead", { ns: "gated" })}
        </Heading>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <fieldset disabled={isPending}>
              {!!me?.inflowUser.savedPlates.length && (
                <>
                  <div className="field">
                    <Label id="label-saved-plates">
                      {t("register.vehicle.savedVehiclesLabel", {
                        ns: "gated",
                      })}
                    </Label>
                    <GridList
                      aria-label={t("register.vehicle.savedVehiclesLabel", {
                        ns: "gated",
                      })}
                      aria-labelledby="label-saved-plates"
                      className="field--input"
                      selectionBehavior="replace"
                      disallowEmptySelection
                      dependencies={[
                        me.inflowUser.savedPlates,
                        variables,
                        isPending,
                      ]}
                      disabledKeys={
                        isPending
                          ? me.inflowUser.savedPlates.map((v) => v.plate)
                          : undefined
                      }
                      // disabledKeys={isPending ? "all" : undefined}
                      items={me.inflowUser.savedPlates.sort((a, b) =>
                        parseAbsoluteToLocal(a.lastUsed).compare(
                          parseAbsoluteToLocal(b.lastUsed)
                        )
                      )}
                    >
                      {(item) => (
                        <GridListItem
                          id={`${item.lastUsed}${item.nickname}${item.plate}`}
                          key={`${item.lastUsed}${item.nickname}${item.plate}`}
                          textValue={
                            item.nickname
                              ? `${item.nickname} - ${item.plate}`
                              : item.plate
                          }
                          className="saved-vehicle-input"
                          onAction={() => {
                            mutate({
                              plate: item.plate,
                              phone: null,
                              method: "plate",
                            })
                          }}
                        >
                          <Icon name="car" />
                          <Text as="span" className="semi-bold start">
                            {item.nickname ? (
                              <>
                                {item.nickname}&nbsp;
                                {
                                  <Text as="span" className="regular">
                                    ({item.plate})
                                  </Text>
                                }
                              </>
                            ) : (
                              item.plate
                            )}
                          </Text>
                          {isPending && item.plate === variables.plate ? (
                            <ButtonLoader
                              style={{
                                display: "block",
                                position: "initial",
                                transform: "initial",
                              }}
                              size={20}
                            />
                          ) : (
                            <Icon name="chevronRight" />
                          )}
                        </GridListItem>
                      )}
                    </GridList>
                  </div>
                  <div className="horizontal-separator-with-text">
                    <Separator orientation="horizontal" />
                    <Text level={3} className="bold">
                      {t("register.vehicle.orManuallyEnter", { ns: "gated" })}
                    </Text>
                    <Separator orientation="horizontal" />
                  </div>
                </>
              )}
              <Controller<RegistrationFormSchema>
                name="plate"
                rules={{
                  required: requiredRule(
                    t("common.required", { ns: "errors" })
                  ),
                }}
                render={({
                  field: { ref, onChange, ...rest },
                  fieldState: { error, invalid },
                }) => (
                  <TextField
                    {...rest}
                    onChange={(v) => onChange(normalizeLicensePlate(v))}
                    isInvalid={invalid}
                  >
                    <Label>
                      {t("register.vehicle.fieldLabel", { ns: "gated" })}
                    </Label>
                    <TextField.Input innerRef={ref} />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
              <Button
                type="submit"
                buttonType="primary"
                isPending={plate === variables?.plate && isPending}
                isDisabled={isPending}
              >
                {t("register.vehicle.submitButton", { ns: "gated" })}
                <Icon name="arrowRight" slot="right" />
              </Button>
            </fieldset>
          </form>
        </FormProvider>
        <Text level={3} className="center">
          <TermsAndConditions qrLookupID={qrLookupID} />
        </Text>
      </Card>

      <ConfirmPlateModal
        isOpen={showModal}
        onOpenChange={toggleModal}
        plate={selectedPlate}
        resetMutation={reset}
        title={t("register.vehicle.confirmModal.header", { ns: "gated" })}
        description={t("register.vehicle.confirmModal.body", { ns: "gated" })}
        editButtonText={t("register.vehicle.confirmModal.changePlate", {
          ns: "gated",
        })}
        confirmButtonText={t("common.continue", { ns: "common" })}
        onEdit={() => toggleModal()}
        onConfirm={() =>
          navigate({
            to: "/qr/$qrLookupID/gated/register/contact-info",
            search: { plate: selectedPlate },
          })
        }
      />
    </>
  )

  function onSubmit(data: RegistrationFormSchema) {
    mutate({
      plate: data.plate,
      phone: null,
      method: "plate",
    })
  }

  async function mutationFn(request: GatedLookupSessionsPOSTRequest) {
    const rsp = await api.gated.lookupSessions.POST(request)
    if ("failReason" in rsp) {
      throw new GatedLookupSessionsError(rsp.failReason)
    }
    return rsp
  }

  function onSuccess(
    rsp: GatedLookupSessionsPOSTSuccessResponse,
    variables: GatedLookupSessionsPOSTRequest
  ) {
    const session = rsp.sessions.length && rsp.sessions[0]

    if (session) {
      const { sessionID, isRegistered } = session
      if (isRegistered) {
        navigate({
          to: "/qr/$qrLookupID/gated/register/$sessionID/complete",
          from: Route.id,
          params: { sessionID, qrLookupID },
          search: { express: false },
        })
      } else {
        navigate({
          to: "/qr/$qrLookupID/gated/register/contact-info",
          from: Route.id,
          params: { qrLookupID },
          search: { plate: variables.plate, sessionID },
        })
      }
    } else {
      setSelectedPlate(variables.plate)
      toggleModal()
    }
  }
}
